/**
 * @module cercaliagl/service/Geocoding
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import Service from './Service';
/**
 * @typedef {Object} GeocodingOptions
 * @property {string} [name] Name filter.
 * @property {string} [countryId] Country code filter.
 * @property {string} [country] Country name filter.
 * @property {string} [regionId] Region code filter.
 * @property {string} [region] Region name filter.
 * @property {string} [subregionId] Subregion code filter.
 * @property {string} [subregion] Subregion name filter.
 * @property {string} [subOrRegion] Region & Subregion name filter.
 * @property {string} [municipalityId] Municipality code filter.
 * @property {string} [municipality] Municipality name filter.
 * @property {string} [cityId] City code filter.
 * @property {string} [city] City name filter.
 * @property {string} [postalCode] Postal Code filter, requires country code.
 * @property {string} [address] Address filter, including house number.
 * @property {string} [streetId] Street code filter.
 * @property {string} [street2Id] Second street code filter, for intersection search between main street and second street.
 * @property {string} [street] Street name filter.
 * @property {string} [street2] Second street name filter, for intersection search between main street and second street.
 * @property {number} [housenumber] House number filter.
 * @property {string} [roadName] Road name filter.
 * @property {string} [roadId] Road code filter.
 * @property {number} [km] Mile marker filter `only for roads`.
 * @property {string} [fullSearch] Resolves all the candidate parameters.
 * @property {string} [numCand] Maximum number of candidates per page.
 * @property {number} [posCand] First candidate position, starting from 0.
 * @property {number} [num] Maximum number of candidates.
 * @property {string} [poicat] List of Points of Interest - POIs.
 * @property {string} [poiId] POI code filter.
 * @property {string} [poiName] POI name filter.
 */
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const geocodingService = new cercaliagl.service.Geocoding({ ... });
 * ```
 *
 * Geocoding service class offers a service to make requests to the Cercalia webservice (https://www.cercalia.com/Documentacion/webservices/Geocodificacion) via Javascript.
 * There are all possible options below, in the constructor description.<br/>
 * <br/>
 * If you need a list of ID entities you should contact with Cercalia support.
 * <br/>
 *
 * Example usage:
 *
 * ```js
 * const geocodingService = new cercaliagl.service.Geocoding({
 *   countryId: countryId,
 *   municipality: municipality !== '' ? municipality : null,
 *   postalCode: pcode !== '' ? pcode : null,
 *   address: address !== '' ? address : null,
 * });
 *
 * geocodingService.geocode((data) => {
 *   console.log(data);
 * });
 * ```
 *
 * @extends {Service}
 * @api
 */
var Geocoding = /** @class */ (function (_super) {
    __extends(Geocoding, _super);
    /**
     * @constructor
     * @param {GeocodingOptions} [options] Geocoding control options.
     */
    function Geocoding(options) {
        if (options === void 0) { options = {}; }
        var _this = _super.call(this) || this;
        /**
         * @type {Object}
         * @private
         */
        _this.response_ = null;
        /**
         * Name general filter (locn)
         * @private
         * @type {string|null}
         */
        _this.name_ = options.name ? options.name : null;
        /**
         * Country code filter (ctryc)
         * @private
         * @type {string|null}
         */
        _this.countryId_ = options.countryId ? options.countryId : null;
        /**
         * Country name filter (ctryn)
         * @private
         * @type {string|null}
         */
        _this.country_ = options.country ? options.country : null;
        /**
         * Region code filter (regc)
         * @private
         * @type {string|null}
         */
        _this.regionId_ = options.regionId ? options.regionId : null;
        /**
         * Region name filter (regn)
         * @private
         * @type {string|null}
         */
        _this.region_ = options.region ? options.region : null;
        /**
         * Subregion code filter (subregc)
         * @private
         * @type {string|null}
         */
        _this.subregionId_ = options.subregionId ? options.subregionId : null;
        /**
         * Subregion name filter (subregn)
         * @private
         * @type {string|null}
         */
        _this.subregion_ = options.subregion ? options.subregion : null;
        /**
         * Region and Subregion name filter (rsn)
         * @private
         * @type {string|null}
         */
        _this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;
        /**
         * Municipality code filter (munc)
         * @private
         * @type {string|null}
         */
        _this.municipalityId_ = options.municipalityId ? options.municipalityId : null;
        /**
         * Municipality name filter (munn)
         * @private
         * @type {string|null}
         */
        _this.municipality_ = options.municipality ? options.municipality : null;
        /**
         * Town code filter (ctc)
         * @private
         * @type {string|null}
         */
        _this.cityId_ = options.cityId ? options.cityId : null;
        /**
         * Town name filter (ctn)
         * @private
         * @type {string|null}
         */
        _this.city_ = options.city ? options.city : null;
        /**
         * Postal code filter (pcode)
         * @private
         * @type {string|null}
         */
        _this.postalCode_ = options.postalCode ? options.postalCode : null;
        /**
         * Postal code filter (pcode)
         * @private
         * @type {string|null}
         */
        _this.address_ = options.address ? options.address : null;
        /**
         * Street code filter (stc)
         * @private
         * @type {string|null}
         */
        _this.streetId_ = options.streetId ? options.streetId : null;
        /**
         * Street name filter (stn)
         * @private
         * @type {string|null}
         */
        _this.street_ = options.street ? options.street : null;
        /**
         * Secondary street name filter to search cross-streets (istn)
         * @private
         * @type {string|null}
         */
        _this.street2_ = options.street2 ? options.street2 : null;
        /**
         * Secondary street code filter to search cross-streets (istc)
         * @private
         * @type {string|null}
         */
        _this.street2Id_ = options.street2Id ? options.street2Id : null;
        /**
         * House number filter (stnum)
         * @private
         * @type {number|null}
         */
        _this.housenumber_ = options.housenumber ? options.housenumber : null;
        /**
         * Road name filter (rdn)
         * @private
         * @type {string|null}
         */
        _this.roadName_ = options.roadName ? options.roadName : null;
        /**
         * Road code filter (rdc)
         * @private
         * @type {string|null}
         */
        _this.roadId_ = options.roadId ? options.roadId : null;
        /**
         * Mile marker filter (km)
         * @private
         * @type {number|null}
         */
        _this.km_ = options.km ? options.km : null;
        /**
         * Use all candidates parameters (fullsearch)
         * @private
         * @type {string|null}
         */
        _this.fullSearch_ = options.fullSearch ? options.fullSearch : null;
        /**
         * Number of candidates per page (numcand)
         * @private
         * @type {string|null}
         */
        _this.numCand_ = options.numCand ? options.numCand : null;
        /**
         * First candidate position, begining with 0 (poscand)
         * @private
         * @type {number|null}
         */
        _this.posCand_ = options.posCand ? options.posCand : null;
        /**
         * Maximum number of results (num)
         * @private
         * @type {number|null}
         */
        _this.num_ = options.num ? options.num : null;
        /**
         * POIs categories filter (poicat)
         * @private
         * @type {string|null}
         */
        _this.poicat_ = options.poicat ? options.poicat : null;
        /**
         * POI code filter (poic)
         * @private
         * @type {string|null}
         */
        _this.poiId_ = options.poiId ? options.poiId : null;
        /**
         * POI name filter (poiname)
         * @private
         * @type {string|null}
         */
        _this.poiName_ = options.poiName ? options.poiName : null;
        return _this;
    }
    /**
     * @param {GeocodingOptions} options Geocoding search options.
     * @api
     */
    Geocoding.prototype.setSearch = function (options) {
        this.name_ = options.name ? options.name : null;
        this.countryId_ = options.countryId ? options.countryId : null;
        this.country_ = options.country ? options.country : null;
        this.regionId_ = options.regionId ? options.regionId : null;
        this.region_ = options.region ? options.region : null;
        this.subregionId_ = options.subregionId ? options.subregionId : null;
        this.subregion_ = options.subregion ? options.subregion : null;
        this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;
        this.municipalityId_ = options.municipalityId ? options.municipalityId : null;
        this.municipality_ = options.municipality ? options.municipality : null;
        this.cityId_ = options.cityId ? options.cityId : null;
        this.city_ = options.city ? options.city : null;
        this.postalCode_ = options.postalCode ? options.postalCode : null;
        this.address_ = options.address ? options.address : null;
        this.streetId_ = options.streetId ? options.streetId : null;
        this.street_ = options.street ? options.street : null;
        this.street2_ = options.street2 ? options.street2 : null;
        this.street2Id_ = options.street2Id ? options.street2Id : null;
        this.housenumber_ = options.housenumber ? options.housenumber : null;
        this.roadName_ = options.roadName ? options.roadName : null;
        this.roadId_ = options.roadId ? options.roadId : null;
        this.km_ = options.km ? options.km : null;
        this.fullSearch_ = options.fullSearch ? options.fullSearch : null;
        this.numCand_ = options.numCand ? options.numCand : null;
        this.posCand_ = options.posCand ? options.posCand : null;
        this.num_ = options.num ? options.num : null;
        this.poicat_ = options.poicat ? options.poicat : null;
        this.poiId_ = options.poiId ? options.poiId : null;
        this.poiName_ = options.poiName ? options.poiName : null;
        this.response_ = null;
    };
    /**
     * Clear search.
     * @api
     */
    Geocoding.prototype.clear = function () {
        this.setSearch(/** @type {GeocodingOptions} */ ({}));
    };
    /**
     * Returns the response once the 'geocode' function finished
     * @return {Object} response
     * @api
     */
    Geocoding.prototype.getResponse = function () {
        return this.response_;
    };
    /**
     * AJAX request
     * @param {Function} callbackFn Callback Function to parse server data.
     * @api
     */
    Geocoding.prototype.geocode = function (callbackFn) {
        var _this = this;
        var params = { cmd: 'geocoding' };
        if (this.name_) {
            params.name = this.name_;
        }
        if (this.countryId_) {
            params.countryId = this.countryId_;
        }
        if (this.country_) {
            params.country = this.country_;
        }
        if (this.regionId_) {
            params.regionId = this.regionId_;
        }
        if (this.region_) {
            params.region = this.region_;
        }
        if (this.subregionId_) {
            params.subregionId = this.subregionId_;
        }
        if (this.subregion_) {
            params.subregion = this.subregion_;
        }
        if (this.subOrRegion_) {
            params.subOrRegion = this.subOrRegion_;
        }
        if (this.municipalityId_) {
            params.municipalityId = this.municipalityId_;
        }
        if (this.municipality_) {
            params.municipality = this.municipality_;
        }
        if (this.cityId_) {
            params.cityId = this.cityId_;
        }
        if (this.city_) {
            params.city = this.city_;
        }
        if (this.postalCode_) {
            params.postalCode = this.postalCode_;
        }
        if (this.address_) {
            params.address = this.address_;
        }
        if (this.streetId_) {
            params.streetId = this.streetId_;
        }
        if (this.street_) {
            params.street = this.street_;
        }
        if (this.street2_) {
            params.street2 = this.street2_;
        }
        if (this.street2Id_) {
            params.street2Id = this.street2Id_;
        }
        if (this.housenumber_) {
            params.housenumber = this.housenumber_;
        }
        if (this.roadName_) {
            params.roadName = this.roadName_;
        }
        if (this.roadId_) {
            params.roadId = this.roadId_;
        }
        if (this.km_) {
            params.km = this.km_;
        }
        if (this.fullSearch_) {
            params.fullSearch = this.fullSearch_;
        }
        if (this.numCand_) {
            params.numCand = this.numCand_;
        }
        if (this.posCand_) {
            params.posCand = this.posCand_;
        }
        if (this.num_) {
            params.num = this.num_;
        }
        if (this.poicat_) {
            params.poicat = this.poicat_;
        }
        if (this.poiId_) {
            params.poiId = this.poiId_;
        }
        if (this.poiName_) {
            params.poiName = this.poiName_;
        }
        this.getRequest({
            data: params,
            timeout: 20000,
            callbackFn: function (data) {
                _this.response_ = data;
                if (callbackFn) {
                    callbackFn(_this.response_);
                }
                else {
                    console.error('No callbackfunction defined'); // eslint-disable-line no-console
                }
            },
        });
    };
    return Geocoding;
}(Service));
export default Geocoding;
//# sourceMappingURL=Geocoding.js.map